/**
 * Coerciona un valor temporal heterogéneo a un Date válido.
 * Acepta epoch ms (number), ISO string o Date. Devuelve null si es
 * nulo/vacío/no parseable (para que los formateadores decidan el fallback).
 * @param {number|string|Date|null|undefined} value
 * @returns {Date|null}
 */
export function toDate(value) {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatCLP(n) {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(n);
}

/**
 * Formatea un monto en LA MONEDA QUE TRAE EL DATO, no en una fija.
 *
 * `formatCLP` hardcodea CLP y ya no alcanza: la plataforma opera en varios países
 * y los montos de pago contra entrega se persisten con su propio código ISO
 * (`cashOnDelivery.currency`, estampado al crear el paquete). Renderizar un monto
 * en COP con el símbolo chileno no es un detalle cosmético: cambia lo que el
 * comercio cree que se le cobró al destinatario.
 *
 * Decisiones:
 *  - Locale fijo 'es-CL' (el mismo del resto del módulo). Con una moneda ajena
 *    Intl imprime el código ISO ("COP 1.500") en vez de un símbolo ambiguo, que es
 *    justo lo que queremos: sin ambigüedad de cuál moneda es.
 *  - 0 a 2 decimales: los montos enteros se ven limpios y los que sí tienen
 *    centavos (el DTO admite 2) no se redondean en pantalla.
 *  - Sin `currency` no inventamos símbolo: se devuelve el número pelado.
 *
 * @param {number|string} amount
 * @param {string} [currency] código ISO-4217 (p. ej. 'CLP', 'COP', 'PEN').
 * @param {string} [fallback='—'] qué devolver si el monto no es un número.
 */
export function formatMoney(amount, currency, fallback = '—') {
  // null/undefined/'' se descartan ANTES de Number(): `Number(null)` es 0 y
  // renderizar "$0" cuando en realidad no hay dato es una mentira silenciosa
  // justo en el campo donde más cara sale.
  if (amount === null || amount === undefined || amount === '') return fallback;

  const n = Number(amount);
  if (!Number.isFinite(n)) return fallback;

  const code = typeof currency === 'string' ? currency.trim().toUpperCase() : '';
  if (!code) return formatNumberCL(n);

  try {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    // Código no reconocido por Intl (lanza RangeError): mostramos número + código
    // en vez de dejar caer la vista entera por un dato mal cargado.
    return `${formatNumberCL(n)} ${code}`;
  }
}

export function formatDate(iso, opts) {
  return new Intl.DateTimeFormat(
    'es-CL',
    opts ?? { day: '2-digit', month: '2-digit', year: 'numeric' },
  ).format(new Date(iso));
}

export function formatTime(iso) {
  return new Intl.DateTimeFormat('es-CL', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

export function formatDateTime(value) {
  const d = toDate(value);
  if (!d) return '';
  return new Intl.DateTimeFormat('es-CL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(d);
}

/** Fecha corta "día mes" (es-CL), ej. "27 jun". '' si el valor es inválido. */
export function formatDayMonth(value) {
  const d = toDate(value);
  if (!d) return '';
  return new Intl.DateTimeFormat('es-CL', { day: '2-digit', month: 'short' }).format(d);
}

/** Fecha+hora corta "día mes, HH:MM" (es-CL), ej. "27 jun, 14:30". '' si inválido. */
export function formatDateTimeShort(value) {
  const d = toDate(value);
  if (!d) return '';
  return new Intl.DateTimeFormat('es-CL', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  }).format(d);
}

export function localDateStr(d = new Date(), tz) {
  const opts = tz ? { timeZone: tz } : undefined;
  return d.toLocaleDateString('en-CA', opts);
}

// ─────────────────────────────────────────────────────────────────────────────
// Tiempo relativo — FUENTE ÚNICA DE VERDAD.
// Toda la app (gps, actividad, sockets, primitivo <RelativeTime/>) deriva de
// `formatRelative`. Acepta epoch ms | ISO string | Date, soporta i18n opcional
// y es configurable por granularidad sin romper la firma legada `(value, now)`.
// ─────────────────────────────────────────────────────────────────────────────

/** Diccionario español por defecto. Cada entrada recibe el `count` (si aplica). */
const ES_RELATIVE = {
  now:        () => 'ahora',
  today:      () => 'hoy',
  yesterday:  () => 'ayer',
  tomorrow:   () => 'mañana',
  secondsAgo: (n) => `hace ${n} s`,
  inSeconds:  (n) => `en ${n} s`,
  minutesAgo: (n) => `hace ${n} min`,
  inMinutes:  (n) => `en ${n} min`,
  hoursAgo:   (n) => `hace ${n} h`,
  inHours:    (n) => `en ${n} h`,
  daysAgo:    (n) => `hace ${n} días`,
  inDays:     (n) => `en ${n} días`,
};

/**
 * Resuelve una frase relativa. Si se pasa `t` (i18n), intenta
 * `t('relativeTime.<key>', { count })` y cae al español si la clave no existe.
 */
function relPhrase(t, key, count) {
  const fallback = ES_RELATIVE[key](count);
  if (typeof t === 'function') {
    const k = `relativeTime.${key}`;
    const res = t(k, { count });
    if (res && res !== k) return res;
  }
  return fallback;
}

/**
 * Formatea un instante como tiempo relativo legible.
 *
 * @param {number|string|Date} value  epoch ms | ISO string | Date.
 * @param {object|Date} [opts]  Si es un Date se interpreta como `now` (compat
 *   con la firma histórica `formatRelative(iso, now)`).
 * @param {Date|number} [opts.now=new Date()]  "ahora" de referencia.
 * @param {(key:string, vars?:object)=>string} [opts.t]  i18n; sin él, español.
 * @param {boolean} [opts.seconds=false]  granularidad de segundos ('hace N s').
 * @param {boolean} [opts.dayWords=true]  usar 'hoy'/'ayer'/'hace N días'; si es
 *   false salta directo a la fecha absoluta una vez superadas las 24 h.
 * @param {number} [opts.absoluteAfterDays=7]  días tras los cuales se muestra
 *   la fecha absoluta en vez de 'hace N días'.
 * @param {(d:Date)=>string} [opts.absolute=formatDate]  formateador de la fecha
 *   absoluta de fallback.
 * @param {string} [opts.invalid='']  texto para valores nulos/inválidos.
 * @returns {string}
 */
export function formatRelative(value, opts) {
  const o = opts instanceof Date ? { now: opts } : (opts || {});
  const {
    now = new Date(),
    t,
    seconds = false,
    dayWords = true,
    absoluteAfterDays = 7,
    absolute,
    invalid = '',
  } = o;

  const date = toDate(value);
  if (!date) return invalid;

  const nowMs = now instanceof Date ? now.getTime() : Number(now);
  const diffMs = date.getTime() - nowMs;
  const past = diffMs <= 0;
  const absMs = Math.abs(diffMs);

  const sec = Math.round(absMs / 1000);
  const min = Math.round(absMs / 60000);
  const hr = Math.round(absMs / 3600000);

  if (seconds) {
    if (sec < 5) return relPhrase(t, 'now');
    if (sec < 60) return relPhrase(t, past ? 'secondsAgo' : 'inSeconds', sec);
  } else if (min < 1) {
    return relPhrase(t, 'now');
  }
  if (min < 60) return relPhrase(t, past ? 'minutesAgo' : 'inMinutes', min);
  if (hr < 24) return relPhrase(t, past ? 'hoursAgo' : 'inHours', hr);

  const fmtAbs = typeof absolute === 'function' ? absolute : formatDate;
  if (!dayWords) return fmtAbs(date);

  const startOfDay = (ms) => { const x = new Date(ms); x.setHours(0, 0, 0, 0); return x.getTime(); };
  const calDiff = Math.round((startOfDay(nowMs) - startOfDay(date.getTime())) / 86400000);
  const days = Math.abs(calDiff);

  if (days === 0) return relPhrase(t, 'today');
  if (days === 1) return relPhrase(t, past ? 'yesterday' : 'tomorrow');
  if (days <= absoluteAfterDays) return relPhrase(t, past ? 'daysAgo' : 'inDays', days);
  return fmtAbs(date);
}

/**
 * Devuelve el tiempo relativo y la fecha absoluta de un mismo instante,
 * reutilizando `formatDateTime` para lo absoluto. Ideal para tooltips.
 * @param {number|string|Date} value
 * @param {object|Date} [opts]  mismas opciones que `formatRelative`.
 * @returns {{ relative: string, absolute: string }}
 */
export function formatRelativeWithAbsolute(value, opts) {
  return {
    relative: formatRelative(value, opts),
    absolute: formatDateTime(value),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Chile-locale helpers — replace inline Intl.NumberFormat('es-CL') / fmtPct /
// fmtCm3 / formatPhone duplications across the app.
// ─────────────────────────────────────────────────────────────────────────────

// Los Intl.NumberFormat son caros de construir y se reusan en listas largas.
const numberFormatCache = new Map();

/**
 * Devuelve un formateador de números para el locale dado (`es-CL`, `es-AR`, …).
 *
 * Existe porque la plataforma dejó de ser mono-país: el locale sale de
 * `useCountry().locale` y no puede seguir fijo en el módulo. Un locale inválido
 * cae a 'es-CL' en vez de tumbar la vista (Intl lanza RangeError con un tag mal
 * formado).
 *
 * @param {string} [locale='es-CL'] BCP 47
 * @returns {(n: number|null|undefined) => string} formateador; nullish → "0"
 */
export function numberFormatterFor(locale) {
  const key = locale || 'es-CL';
  let fmt = numberFormatCache.get(key);
  if (!fmt) {
    try {
      fmt = new Intl.NumberFormat(key);
    } catch {
      fmt = new Intl.NumberFormat('es-CL');
    }
    numberFormatCache.set(key, fmt);
  }
  return (n) => fmt.format(n ?? 0);
}

/**
 * Format a number with Chilean thousands separators ("12.345").
 * Falsy input → "0".
 */
export function formatNumberCL(n) {
  return numberFormatterFor('es-CL')(n);
}

/**
 * Format a fractional value as a percent string ("12.3%").
 * Accepts 0–1 (default for ratios) and renders with `digits` decimals.
 */
export function formatPercent(v, digits = 1) {
  return `${(((v ?? 0) * 100) || 0).toFixed(digits)}%`;
}

/**
 * Format a Chilean phone number. Currently formats +569XXXXYYYY as
 * "+569 XXXX YYYY"; other shapes are returned as-is.
 */
export function formatPhoneCL(phone) {
  if (!phone) return '';
  if (phone.startsWith('+569')) {
    return phone.replace(/^(\+569)(\d{4})(\d{4})$/, '$1 $2 $3');
  }
  return phone;
}

/**
 * Format a volume value with thousands separators + unit ("12.345 cm³").
 */
export function formatVolume(v, unit = 'cm³') {
  return `${formatNumberCL(v)} ${unit}`;
}
